import { Link } from "react-router-dom";
import { ArrowRight, Award, BadgeCheck, ClipboardList, HeartHandshake, Sparkles, Users } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import CtaBand from "../components/CtaBand";
import { useLang } from "../lib/lang";
import { IMAGES } from "../lib/content";

const cvIcons = [Award, BadgeCheck, ClipboardList, Users, Sparkles, HeartHandshake];

export default function About() {
  const { t } = useLang();

  return (
    <div>
      <Seo title={t.meta.about.title} description={t.meta.about.desc} />

      <section className="mx-auto max-w-6xl px-5 pb-16 pt-12 sm:pt-16" data-testid="about-hero">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <Reveal y={12}>
              <p className="eyebrow">{t.about.eyebrow}</p>
              <h1 className="mt-4 font-display text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">
                {t.about.title}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[#475569] sm:text-lg">
                {t.about.intro}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="rotate-1 overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_30px_60px_-25px_rgba(30,27,75,0.4)]">
                <img src={IMAGES.illAbout} alt={t.about.imageAlt} loading="eager" className="w-full object-cover" data-testid="about-illustration" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-24" data-testid="about-cv">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <h2 className="font-display text-3xl text-ink sm:text-4xl">{t.about.cvTitle}</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {t.about.cv.map((item, i) => {
              const Icon = cvIcons[i];
              return (
                <Reveal key={i} delay={0.06 * i}>
                  <article className="card-warm flex h-full gap-4 p-6 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(30,27,75,0.35)]" data-testid={`cv-card-${i}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFEDD5] text-saffron">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg leading-snug text-ink">{item.t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">{item.d}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:py-24" data-testid="about-philosophy">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <img
              src={IMAGES.boyBook}
              alt={t.about.photoAlt}
              loading="lazy"
              className="h-80 w-full rounded-[2.5rem] object-cover shadow-[0_30px_60px_-25px_rgba(30,27,75,0.35)] lg:h-[440px]"
              data-testid="about-photo"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border-l-4 border-saffron pl-6 sm:pl-8">
              <h2 className="font-display text-3xl text-ink sm:text-4xl">{t.about.philTitle}</h2>
              <p className="mt-5 text-base leading-relaxed text-[#475569] sm:text-lg">
                {t.about.phil}
              </p>
              <Link to="/contact" data-testid="about-cta" className="btn-primary mt-8">
                {t.nav.cta}
                <ArrowRight size={17} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
